import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, DollarSign, Calendar, TrendingUp, CreditCard } from 'lucide-react';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import LoadingSpinner from './LoadingSpinner';

const SalesDetailsModal = ({ isOpen, onClose, title, records, type, loading, onUpdatePayment }) => {
  const navigate = useNavigate();
  useModalScrollLock(isOpen);

  if (!isOpen) return null;

  const formatCurrency = (amount) => {
    return '₹' + Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getIcon = () => {
    if (type === 'today') return <Calendar className="h-6 w-6 text-white" />;
    if (type === 'month') return <TrendingUp className="h-6 w-6 text-white" />;
    return <DollarSign className="h-6 w-6 text-white" />;
  };

  const getDocumentNumber = (record) => {
    if (record.status === 'closed_order') {
      return record.closed_order_number || record.order_number || record.orderNumber || 'Unknown';
    }
    return record.invoice_number || record.invoiceNumber || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-4 text-white flex justify-between items-center shrink-0 bg-[#0A5F38]">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg">
              {getIcon()}
            </div>
            <div>
              <h2 className="text-xl font-bold">{title}</h2>
              {!loading && (
                <p className="text-white/80 text-sm">
                  {records.length} {type === 'all' && records.length === 100 ? 'records (showing latest 100)' : 'records found'}
                </p>
              )}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <LoadingSpinner size="lg" color="#0A5F38" />
              <p className="mt-4 text-slate-600 font-medium">Loading sales records...</p>
            </div>
          ) : records.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-xs font-bold border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Document Details</th>
                      <th className="px-6 py-4">Customer</th>
                      <th className="px-6 py-4 text-right">Total Amount</th>
                      <th className="px-6 py-4 text-center">Payment Status</th>
                      {onUpdatePayment && <th className="px-6 py-4 text-center">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.map((record, idx) => (
                      <tr key={record._id || idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-slate-600 font-medium">
                          {new Date(record.soldAt || record.createdAt).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4">
                          <button 
                            onClick={() => {
                              onClose();
                              if (record.status === 'closed_order') {
                                const docNum = record.closed_order_number || record.order_number || record.orderNumber;
                                navigate(`/admin/closed-orders?view=${docNum}`);
                              } else {
                                const docNum = record.invoice_number || record.invoiceNumber;
                                navigate(`/admin/invoices?view=${docNum}`);
                              }
                            }}
                            className="flex flex-col text-left hover:opacity-75 transition-opacity"
                          >
                            <span className="font-bold text-[#0A5F38] hover:underline">
                              {getDocumentNumber(record)}
                            </span>
                            <span className="text-xs text-slate-500 capitalize">
                              {record.status === 'closed_order' ? 'Closed Order' : 'Invoice'}
                            </span>
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-800">{record.customerId?.name || record.customer_name || 'Walk-in Customer'}</div>
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-[#0A5F38]">
                          {formatCurrency(record.totalAmount)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                            ${(record.paymentStatus || 'pending') === 'paid' ? 'bg-green-100 text-green-800' : 
                              (record.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}`}
                          >
                            {record.paymentStatus || 'Pending'}
                          </span>
                        </td>
                        {onUpdatePayment && (
                          <td className="px-6 py-4 text-center">
                            {(record.paymentStatus || 'pending') !== 'paid' ? (
                              <button
                                onClick={() => onUpdatePayment(record)}
                                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-[#0A5F38] text-white rounded-lg text-xs font-semibold hover:bg-[#084d2d] transition-colors shadow-sm whitespace-nowrap"
                              >
                                <CreditCard size={14} />
                                <span>Update Payment</span>
                              </button>
                            ) : (
                              <span className="text-slate-400 text-xs font-medium">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <DollarSign className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-lg font-medium">No sales records found for this period.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white p-4 border-t border-slate-200 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors"
          >
            CLOSE
          </button>
        </div>

      </div>
    </div>
  );
};

export default SalesDetailsModal;
